import { useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/run-my-day/google-provider-readiness`;
const INSTRUCTOR_ROLES = new Set(["adi", "pdi", "admin"]);

const STATUS_LABELS = {
  not_configured: "Not configured",
  accepted: "Google accepted this check",
  rejected: "Google refused this request",
  unavailable: "Could not verify",
};

const REASON_LABELS = {
  missing_key: "The backend setting is empty.",
  provider_accepted_key: "The request succeeded.",
  invalid_key: "The key is invalid.",
  permission_denied: "Google refused permission for this request.",
  provider_rejected_key: "Google refused the request.",
  api_not_enabled: "Enable the relevant Google API for this project.",
  billing_disabled: "Google billing is not enabled for this project.",
  quota_exceeded: "The Google API quota was reached.",
  daily_limit_or_setup: "Google reported a daily-limit or setup problem; it did not identify which one.",
  timeout: "Google did not respond before the time limit.",
  network_error: "The backend could not reach Google.",
  provider_unavailable: "Google is temporarily unavailable.",
  probe_request_rejected: "Google did not accept the diagnostic request.",
  api_restriction: "This key's API restrictions block this service.",
  api_not_authorized: "Google says this project or key is not authorised to use this service.",
  ip_restriction: "This key's IP address restrictions block the backend server.",
  referrer_restriction: "This key's website restrictions do not permit this backend request.",
  ios_restriction: "This key's iOS app restrictions do not permit this backend request.",
  android_restriction: "This key's Android app restrictions do not permit this backend request.",
  project_invalid: "Google could not use the project associated with this request.",
};

const SERVICE_NAMES = { Places: "Places API (New)", Routes: "Routes API", Geocoding: "Geocoding API" };

function nextAction(title, reason) {
  const service = SERVICE_NAMES[title] || title;
  if (reason === "api_not_enabled") return `Enable ${service} in the Google Cloud project that owns this backend key.`;
  if (reason === "api_restriction") return `Allow ${service} in this key's API restrictions. The API must also be enabled in its project.`;
  if (reason === "api_not_authorized") return `Check both that ${service} is enabled in the key's project and that the key's API restrictions allow it.`;
  if (reason === "billing_disabled") return "Check that the key's Google Cloud project is linked to an active billing account.";
  if (reason === "ip_restriction") return "Check that this key permits the backend server's outbound IP addresses.";
  if (["referrer_restriction", "ios_restriction", "android_restriction"].includes(reason)) return "Use a separate server key for this backend setting, with the required API permissions and server IP restrictions.";
  if (reason === "invalid_key") return "Check that the saved backend key is current and belongs to the intended Google Cloud project.";
  if (reason === "project_invalid") return "Check the Google Cloud project associated with the saved backend key.";
  if (reason === "quota_exceeded") return `Check the ${service} quota and usage in Google Cloud.`;
  if (reason === "daily_limit_or_setup") return "Check the backend key, billing account and any daily quota cap in Google Cloud.";
  if (reason === "missing_key") return "Add the intended server key to this backend setting, then deploy the configuration.";
  return "";
}

function ProviderResult({ title, result }) {
  if (!result) return null;
  const label = STATUS_LABELS[result.status] || "Could not verify";
  const reason = REASON_LABELS[result.reason] || "";
  const action = nextAction(title, result.reason);
  const noSpecificReason = result.status === "rejected" && !result.google_reason
    && ["permission_denied", "provider_rejected_key"].includes(result.reason);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-3" data-testid={`google-readiness-${title.toLowerCase()}`}>
      <p className="text-xs font-semibold text-slate-300">{title}</p>
      <p className="text-xs text-slate-400">{SERVICE_NAMES[title]}</p>
      <p className="mt-1 text-sm font-bold text-white">{label}</p>
      {reason && <p className="mt-1 text-xs text-slate-400">{reason}</p>}
      {result.google_status && <p className="mt-2 break-words text-xs text-slate-300">Google response: {result.google_status}</p>}
      {result.google_reason && <p className="mt-1 break-words text-xs text-amber-200">Google reason: {result.google_reason}</p>}
      {result.detail_state === "unrecognised" && <p className="mt-1 text-xs text-amber-200">Google supplied a reason this checker does not recognise. Its original text is hidden to protect private information.</p>}
      {noSpecificReason && result.detail_state !== "unrecognised" && <p className="mt-1 text-xs text-amber-200">Google gave a general refusal. No specific reason code was supplied.</p>}
      {action && <p className="mt-2 text-sm text-slate-200"><span className="font-semibold">Next step: </span>{action}</p>}
      {result.setting && <p className="mt-2 break-all text-xs text-slate-400">Backend setting: {result.setting}</p>}
      {result.http_status && <p className="mt-1 text-xs text-slate-400">HTTP {result.http_status}</p>}
    </div>
  );
}

export default function GoogleProviderReadiness() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  if (!INSTRUCTOR_ROLES.has(String(user?.role || "").toLowerCase())) return null;

  const runCheck = async () => {
    setBusy(true);
    setError("");
    setResults(null);
    try {
      const { data } = await axios.get(ENDPOINT, {
        withCredentials: true,
        timeout: 20000,
        headers: { "Cache-Control": "no-store" },
      });
      setResults(data?.providers || {});
    } catch (requestError) {
      const status = requestError?.response?.status;
      setError(status === 401 || status === 403
        ? "NDG did not recognise this instructor session. Sign in to NDG again, then retry."
        : "The check could not reach NDG. Please retry when the connection is available.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border-2 border-amber-400 bg-slate-950 p-5 shadow-lg shadow-amber-950/30" aria-labelledby="google-readiness-title">
      <h2 id="google-readiness-title" className="text-xl font-black text-white">Google Maps not working?</h2>
      <p className="mt-1 text-sm text-slate-200">
        Check Places, Routes and address lookup separately. Each result shows Google's specific refusal reason when supplied, and the setting to check. Routes and Geocoding share a backend key but require separate APIs. No keys are shown. Each click makes up to three small Google requests and may incur normal API charges.
      </p>
      <button
        type="button"
        data-testid="google-provider-readiness-run"
        onClick={runCheck}
        disabled={busy}
        className="mt-4 min-h-14 w-full rounded-xl border-2 border-amber-300 bg-amber-400 px-5 py-4 text-lg font-black text-slate-950 shadow-md hover:bg-amber-300 focus:outline-none focus:ring-4 focus:ring-amber-200 disabled:cursor-wait disabled:opacity-60"
      >
        {busy ? "Checking Google services…" : "Check Google Maps now"}
      </button>
      {error && <p role="alert" className="mt-3 text-sm text-amber-200">{error}</p>}
      {results && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" aria-live="polite">
          <ProviderResult title="Places" result={results.places} />
          <ProviderResult title="Routes" result={results.routes} />
          <ProviderResult title="Geocoding" result={results.geocoding} />
        </div>
      )}
    </section>
  );
}

